/**
 * Single source of truth for what an enquiry (lead) status means on the admin
 * Enquiries screen. Mirrors the real backend set: a lead is created "open"
 * (rag/leads.py build_lead_doc) and PATCH /leads/{id}/status only accepts
 * open | accepted | contacted | closed (rag/routes.py patch_lead_status).
 */
export type LeadStatus = "open" | "accepted" | "contacted" | "closed";

export interface LeadStatusInfo {
  label: string;
  /** What the status means, in plain English. */
  meaning: string;
  /** Who or what moves a lead into this status, and what usually comes next. */
  next: string;
  /** Tailwind classes for the pill (dark admin theme). */
  pillClass: string;
}

export const LEAD_STATUS_GUIDE: Record<LeadStatus, LeadStatusInfo> = {
  open: {
    label: "Open",
    meaning: "The buyer sent an enquiry and the seller has not taken it on yet.",
    next: "Set automatically when the enquiry is created. Moves to Accepted when a seller accepts it.",
    pillClass: "bg-amber-500/10 text-amber-400",
  },
  accepted: {
    label: "Accepted",
    meaning: "A seller accepted the enquiry and a chat with the buyer is open, but the seller has not written to the buyer yet.",
    next: "Set when any seller the enquiry was sent to taps Accept (or automatically when the deal's AI auto-reply is ON). Moves to Contacted on the deal owner's first reply. Enquiries accepted before this release stay Accepted until backfilled or until the seller's next reply.",
    pillClass: "bg-emerald-500/10 text-emerald-400",
  },
  contacted: {
    label: "Contacted",
    meaning: "The seller has actually replied to the buyer in the enquiry chat. AI auto-replies and the accept-time greeting do not count.",
    next: "Set automatically on the deal owner's first human reply in the chat (for any other seller only their own status moves). Can also be set by hand by the buyer or a seller, and any status can be moved back by hand.",
    pillClass: "bg-cyan-500/10 text-cyan-400",
  },
  closed: {
    label: "Closed",
    meaning: "The enquiry was finished and is no longer active.",
    next: "Set manually by the buyer who sent it or by a seller on it; when the buyer closes it, it closes for every seller. It is never set automatically: an expired or unpublished deal does not close its enquiries.",
    pillClass: "bg-gray-700/50 text-gray-400",
  },
};

/** Fallback pill style for a status that is in the data but not in the guide. */
export const UNKNOWN_STATUS_PILL_CLASS = "bg-gray-700/50 text-gray-300";

/** Ordered list used for the filter and the legend ("" = all statuses). */
export const LEAD_STATUS_ORDER: LeadStatus[] = ["open", "accepted", "contacted", "closed"];

/** Label and tooltip for any status value; unknown values degrade to the raw value. */
export function describeLeadStatus(status: string | null | undefined): {
  key: string;
  label: string;
  tooltip: string;
  pillClass: string;
  known: boolean;
} {
  const key = status || "open";
  const info = (LEAD_STATUS_GUIDE as Record<string, LeadStatusInfo | undefined>)[key];
  if (!info) {
    return {
      key,
      label: key,
      tooltip: `${key}: not in the status guide.`,
      pillClass: UNKNOWN_STATUS_PILL_CLASS,
      known: false,
    };
  }
  return {
    key,
    label: info.label,
    tooltip: `${info.label}: ${info.meaning} ${info.next}`,
    pillClass: info.pillClass,
    known: true,
  };
}
